import React, { useCallback, useMemo } from 'react';
import { Button } from '../UI/Button';
import { motion } from 'framer-motion';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  isRtl: boolean;
  handlePageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, isRtl, handlePageChange }) => {
  const handlePrevious = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const paginationText = useMemo(() => ({
    previous: isRtl ? 'السابق' : 'Previous',
    next: isRtl ? 'التالي' : 'Next',
    page: isRtl ? `الصفحة ${currentPage} من ${totalPages}` : `Page ${currentPage} of ${totalPages}`,
    navigation: isRtl ? 'تصفح الصفحات' : 'Page navigation',
  }), [isRtl, currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`flex justify-center items-center gap-3 mt-6 ${isRtl ? 'flex-row' : 'flex-row'}`}
      role="navigation"
      aria-label={paginationText.navigation}
    >
      <Button
        variant="secondary"
        size="sm"
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className="disabled:opacity-50 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm shadow-sm"
        aria-label={paginationText.previous}
      >
        {paginationText.previous}
      </Button>
      <span className="text-gray-700 text-sm font-semibold">
        {paginationText.page}
      </span>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className="disabled:opacity-50 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm shadow-sm"
        aria-label={paginationText.next}
      >
        {paginationText.next}
      </Button>
    </motion.div>
  );
};

export default Pagination;